import { Link, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Circle } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { INDUSTRY_TEMPLATES } from '@marketingbee/contracts';
import { Alert, Button, Card, CardContent, Field, Input, LoadingState, Select, useToast } from '@marketingbee/ui';
import { errorMessage, trpc } from '../../lib/trpc';
import { setWorkspaceSlug, useWorkspaceSlug } from '../../lib/workspace';
import { Wordmark } from '../../shell/Logo';
import { clearPending, readPending, REGION_OPTIONS, TEMPLATE_HINT, TEMPLATE_OPTIONS, templateFrom } from './pages';

function Step({ done, title, children }: { done: boolean; title: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-4 py-6">
        <div className="flex items-center gap-3">
          {done ? <CheckCircle2 className="size-5 text-success" aria-label="Done" /> : <Circle className="size-5 text-muted-foreground" aria-label="To do" />}
          <h2 className="text-[17px] font-semibold">{title}</h2>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

/** First workspace (from sign-up) and the setup checklist. */
export function OnboardingPage() {
  const me = trpc.workspace.me.get.useQuery();
  const slug = useWorkspaceSlug();
  const create = trpc.workspace.create.useMutation();
  const utils = trpc.useUtils();
  const pending = readPending();
  const [name, setName] = useState(pending?.workspaceName ?? '');
  const [region, setRegion] = useState(pending ? `${pending.country ?? 'CA'}:${pending.region ?? 'ON'}` : 'CA:ON');
  const [plan, setPlan] = useState<'mkt_starter' | 'mkt_growth'>(pending?.plan ?? 'mkt_starter');
  const [template, setTemplate] = useState(templateFrom(pending?.template));

  const workspaces = me.data?.workspaces ?? [];
  useEffect(() => {
    if (workspaces.length && (!slug || !workspaces.some((w) => w.slug === slug))) setWorkspaceSlug(workspaces[0]!.slug);
  }, [workspaces, slug]);

  if (me.isLoading) return <LoadingState className="min-h-dvh" />;
  const createWorkspace = () => {
    const [country, r] = region.split(':') as ['CA' | 'US', string];
    create.mutate(
      { name, plan, country, region: r, template },
      {
        onSuccess: async (w) => {
          clearPending();
          setWorkspaceSlug(w.slug);
          await utils.workspace.me.get.invalidate();
        },
      },
    );
  };

  return (
    <div className="min-h-dvh bg-background">
      <header className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4">
        <Wordmark />
        {workspaces.length ? (
          <Button asChild variant="ghost">
            <Link to="/dashboard">Go to dashboard</Link>
          </Button>
        ) : null}
      </header>
      <main className="mx-auto max-w-3xl space-y-5 px-4 pb-20 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.03em]">Set up MarketingBee</h1>
        {workspaces.length === 0 || !slug ? (
          <Card>
            <CardContent className="space-y-4 py-6">
              <p className="text-muted-foreground">Create your workspace to start the 7-day trial.</p>
              {create.error ? <Alert variant="destructive">{errorMessage(create.error)}</Alert> : null}
              <Field label="Company name">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Province or state">
                <Select value={region} onChange={(e) => setRegion(e.target.value)} options={REGION_OPTIONS} />
              </Field>
              <Field label="Plan">
                <Select value={plan} onChange={(e) => setPlan(e.target.value as typeof plan)} options={[{ value: 'mkt_starter', label: 'MarketingBee Starter' }, { value: 'mkt_growth', label: 'MarketingBee Growth' }]} />
              </Field>
              <Field label="Industry" description={TEMPLATE_HINT}>
                <Select value={template} onChange={(e) => setTemplate(templateFrom(e.target.value))} options={TEMPLATE_OPTIONS} />
              </Field>
              <Button onClick={createWorkspace} loading={create.isPending} disabled={!name.trim()}>
                Create workspace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Checklist key={slug} />
        )}
      </main>
    </div>
  );
}

const INDUSTRY_OPTIONS = INDUSTRY_TEMPLATES.map((t) => ({ value: t.id, label: t.label }));

function Checklist() {
  const current = trpc.workspace.current.useQuery();
  const settings = trpc.workspace.settings.get.useQuery();
  const brands = trpc.brand.list.useQuery();
  const update = trpc.workspace.settings.update.useMutation();
  const plan = trpc.content.plan.useMutation();
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [industry, setIndustry] = useState('general');
  const [website, setWebsite] = useState('');
  useEffect(() => {
    if (!settings.data) return;
    setIndustry(settings.data.industry ?? 'general');
    setWebsite(settings.data.website ?? '');
  }, [settings.data]);

  if (!current.data || !settings.data) return <LoadingState />;
  const steps = current.data.onboarding.steps;
  const brand = brands.data?.[0];
  const save = (patch: Parameters<typeof update.mutate>[0], message: string) =>
    update.mutate(patch, {
      onSuccess: async () => {
        success(message);
        await Promise.all([utils.workspace.current.invalidate(), utils.workspace.settings.get.invalidate()]);
      },
      onError: (e) => error('Could not save', errorMessage(e)),
    });

  return (
    <div className="space-y-5">
      <p className="text-muted-foreground">
        {current.data.name} · {current.data.entitlements.planName}
        {current.data.entitlements.trialDaysLeft !== null ? ` · ${current.data.entitlements.trialDaysLeft} trial days left` : ''}
      </p>
      <Step done={steps.company} title="1. Your company">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Industry">
            <Select value={industry} onChange={(e) => setIndustry(e.target.value)} options={INDUSTRY_OPTIONS} />
          </Field>
          <Field label="Website">
            <Input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" />
          </Field>
        </div>
        <Button size="sm" onClick={() => save({ industry, website: website || null, onboarding: { company: true } }, 'Company saved')}>
          Save
        </Button>
      </Step>
      <Step done={steps.brand} title="2. Your brand">
        <p className="text-sm text-muted-foreground">Say what you do, who you talk to and the topics you post about. Every draft starts from this, and your guardrails are checked on every post.</p>
        <Button size="sm" asChild>
          <Link to="/brand">{brand ? 'Open Brand' : 'Set up the brand'}</Link>
        </Button>
      </Step>
      <Step done={steps.channels} title="3. Channels">
        <p className="text-sm text-muted-foreground">Connect Konekto Post (Instagram, Facebook and LinkedIn) or Bluesky. Nothing is ever published without your approval.</p>
        <Button size="sm" asChild>
          <Link to="/channels">Connect a channel</Link>
        </Button>
      </Step>
      <Step done={steps.firstPlan} title="4. Your first weekly plan">
        <p className="text-sm text-muted-foreground">MarketingBee plans the week from your cadence and pillars, drafts each post and puts them in Review for you.</p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={!brand}
            loading={plan.isPending}
            onClick={() =>
              brand &&
              plan.mutate(
                { brandId: brand.id },
                {
                  onSuccess: async (r) => {
                    success(r.planned ? `Planned ${r.planned} posts` : 'This week is already planned', `${r.drafted} drafted and waiting in Review.`);
                    await utils.workspace.current.invalidate();
                  },
                  onError: (e) => error('Could not plan the week', errorMessage(e)),
                },
              )
            }
          >
            Plan this week
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/calendar">Open the Calendar</Link>
          </Button>
        </div>
      </Step>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => save({ onboarding: { dismissed: true } }, 'Checklist hidden')}>
          Skip for now
        </Button>
        <Button onClick={() => void navigate({ to: '/dashboard' })}>Go to dashboard</Button>
      </div>
    </div>
  );
}
